// src/components/admin/MessageTemplates.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import { getTemplates, deleteTemplate, MessageTemplate } from '../../services/messageTemplateService';
import { PageTransition } from '../ui/PageTransition';
import { PageTitle } from '../ui/PageTitle';

const MessageTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const fetchedTemplates = await getTemplates();
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error("Erro ao carregar templates:", error);
      alert("Não foi possível carregar os modelos de mensagem.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este modelo de mensagem?')) {
      try {
        await deleteTemplate(id);
        setTemplates(prev => prev.filter(t => t.id !== id));
        alert('Modelo excluído com sucesso!');
      } catch (error) {
        console.error("Erro ao excluir template:", error);
        alert('Não foi possível excluir o modelo.');
      }
    }
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Carregando modelos...</p>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 overflow-x-hidden">
        <div className="mx-2 sm:mx-4 lg:mx-6 mt-2 mb-3">
          <PageTitle
            title="Modelos de Mensagem"
            subtitle="Gerencie seus templates para comunicação via WhatsApp"
          >
            <button
              onClick={() => navigate('/admin/lembretes')}
              className="px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 flex items-center gap-2 font-semibold border border-white/30 transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para Lembretes
            </button>
          </PageTitle>
        </div>

        <div className="p-4">
          <div className="flex justify-end mb-6">
            <button
              onClick={() => navigate('/admin/mensagens/criar')}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-md"
            >
              <Plus className="w-5 h-5" />
              Criar Novo Modelo
            </button>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Nome do Modelo</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200/60">
                  {templates.length > 0 ? (
                    templates.map(template => (
                      <tr key={template.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <MessageSquare className="w-5 h-5 text-teal-500" />
                            <span className="font-medium text-slate-800">{template.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() => navigate(`/admin/mensagens/editar/${template.id}`)}
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                              Editar
                            </button>
                            <button
                              onClick={() => handleDelete(template.id)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-800 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="text-center py-12 text-slate-500">
                        Nenhum modelo de mensagem encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default MessageTemplates;
